import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import { reatomImage } from '@root/shared/lib'
import { RectangleNetworkBackground } from '@root/shared/ui'
import { InfoMessage } from '@root/shared/ui-kit'

import type { UnsafeUrlProvider } from '@root/entities/deeplinking/unsafe-url-dialog/model'

interface SocialImageEmbedProps {
  linkUrl: string
  unsafeUrlProvider: UnsafeUrlProvider
  imageUrl: URL
  alt?: string
  onLoad?: () => void
}

export const SocialImageEmbed = ({
  linkUrl,
  unsafeUrlProvider,
  imageUrl,
  alt,
  onLoad,
}: SocialImageEmbedProps) => {
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomImage({ src: imageUrl.toString() }),
    [imageUrl],
  )
  const [state] = useAtom(model)
  const refresh = useAction(model.refresh)

  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const handleOpenResource = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    openUnsafeUrlDialog([linkUrl, unsafeUrlProvider])
  }

  return (
    <Root>
      {AsyncStateN.matchWaiting(state, {
        Waiting: () => <CircularLoader size={24} />,
        Error: () => <Error intent={'error'} onRetry={refresh} />,
        Success: (src) => (
          <Link href={linkUrl} target="_blank" onClick={handleOpenResource}>
            <Image
              src={src}
              alt={alt ?? ''}
              loading="lazy"
              onLoad={() => onLoad?.()}
            />
          </Link>
        ),
      })}
    </Root>
  )
}

const Root = styled.div`
  ${RectangleNetworkBackground}
  position: relative;
  display: flex;
  width: 480px;
  max-width: 100%;
  min-height: 240px;

  justify-content: center;
  align-items: center;
`

const Link = styled.a`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
const Error = styled(InfoMessage)``

const Image = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
`
